import { customElement, property, query, state } from 'lit/decorators.js';
import { css, html, LitElement, nothing, PropertyValues, unsafeCSS } from 'lit';
import { tableBroker } from '../services/TableBroker';

import '@finos/perspective-viewer/dist/esm/perspective-viewer.inline.js';
import '@finos/perspective-viewer-datagrid/dist/esm/perspective-viewer-datagrid.js';

import '@carbon/web-components/es/components/copy-button/index.js';
import '@carbon/web-components/es/components/icon-button/index.js';
import '@carbon/web-components/es/components/textarea/index.js';
import Icon from '@carbon/web-components/es/icons/debug/16.js';
import Upload from '@carbon/web-components/es/icons/upload/16.js';
import Download from '@carbon/web-components/es/icons/download/16.js';

import Debug from 'debug';
import { ReplaySubject } from '../observable/ReplaySubject';
import { Table, View } from '@finos/perspective';
import { col, grow, hide, margin, padding, row } from '../styles';
import pdqHotkeyService from '../services/PdqHotkeyService';
import { PdqDiscoverable, pdqDiscoveryLayer } from './_pdq-mixin';
import { TAG_TYPE } from '@carbon/web-components/es/components/tag/defs';
import { PerspectiveViewerElement } from '@finos/perspective-viewer/dist/esm/perspective';
import '@finos/perspective-viewer/dist/esm/perspective-viewer.inline';

import staticStyles from 'bundle-text:@finos/perspective-viewer/dist/css/themes.css';
import { defer, extractTextAndScripts, extractTextFromScripts } from '../utils';
import { notificationService } from '../services/notificationService';
import { ExtendedDatagridPlugin } from './extended-datagrid-plugin';
import { firstValueFrom, from, Subscription } from 'rxjs';
import { dataBroker } from '../services/DataBroker';
import { switchMap } from 'rxjs/operators';
import { HTMLPerspectiveViewerElement } from '@finos/perspective-viewer';

const PRE_INIT = Symbol('PRE-INIT');
const log = Debug('pdq:flow:perspective-viewer');

@customElement('pdq-perspective-viewer')
@pdqDiscoveryLayer(log, Icon, 'pdq-perspective-viewer', 'rgba(83, 83, 236, 0.13)', TAG_TYPE.PURPLE)
export class PdqPerspectiveViewer extends LitElement implements PdqDiscoverable {
  static styles = [hide, row, col, grow, padding, margin,
    unsafeCSS(staticStyles),
    css`perspective-viewer {font-family: "IBM Plex Sans", "Helvetica Neue", Arial, sans-serif!important;}`];
  private _perspectivePresent = defer();
  private _perspectiveLoaded = defer()
  private _loaded = false
  static next_id = 0;
  _id = PdqPerspectiveViewer.next_id++;

  @property({ type: String }) id: string = `pdq-perspective-viewer-${this._id}`;
  @property({ type: Boolean}) show: boolean = false;
  @property({ type: String}) theme: string = 'Pro Light';
  @property({ type: String}) height: string = '500px';
  @property({ type: Boolean}) hideIndex: boolean = false;
  @property({ type: Array}) hiddenColumns: string[] = [];
  @property({ type: Boolean }) selectable: boolean = false;
  @property({ type: Array }) slicers: string[] = [];

  @state() dataPreview = 'no data';

  #table: string | undefined = undefined;

  @property() error: string | null = null;

  @property({ type: String })
  get table() {
    return this.#table || '';
  }
  set table(value: string) {
    this.#table = value;
    this.updateData();
  }

  @property({ type: Object })
  config: any = {};

  #perspectiveViewer:HTMLPerspectiveViewerElement | undefined
  @query('perspective-viewer')
  get perspectiveViewer(){
    return this.#perspectiveViewer;
  }
  set perspectiveViewer(value: HTMLPerspectiveViewerElement | undefined){
    if (value)
      this.#perspectiveViewer = value;
  }
  @query('cds-textarea')
  configEditor: HTMLTextAreaElement | undefined;

  _listenersAdded = false
  addEventListeners(){
    if (!this.perspectiveViewer)
      return
    if (this._listenersAdded)
      return;

    this._listenersAdded = true
    log('adding listeners');

    const tableName = this.#table
    if (!tableName)
      return

    dataBroker.upsertDerivation(`${this.id}:row`, [`${this.id}:selected`], async ([row]: any) => {
      log(`###: ${this.id}:row - ${row}`)

      const table = await firstValueFrom(tableBroker.getTable(tableName));
      const index = tableBroker.getTableMeta(tableName).index as string;
      const view = await table.view({filter: [
          [index, "==", row[0][index]]
        ]});

      const json =  await view.to_json();
      log(`DATA ISSUES`, json);
      return json
    });

    this.perspectiveViewer?.addEventListener('perspective-config-update', (evt: any) => {
      log('perspective-config-change', evt);
      this.dispatchEvent(new CustomEvent('perspective-config-update', {detail: evt.detail}));
      this.config = evt.detail;
      this._updatePreview();
      this.hideColumnsAsRequired()
    });
    this.perspectiveViewer?.addEventListener('perspective-select', (event:any) => {
      log('perspective-select', event);
      this.dispatchEvent(new CustomEvent('perspective-select', {detail: event.detail}));
      if (!this.id)
        return
      dataBroker.setDataSource(`${this.id}:selected`, [{...event.detail.row }]);
    });
    this.perspectiveViewer?.addEventListener('perspective-click', async (evt: any) => {
      log('perspective-click', evt);
      this.dispatchEvent(new CustomEvent('perspective-click', {detail: evt.detail}));
    });
    this._perspectivePresent.resolve(true);
    tableBroker.addViewer(this.id, this);
    this.hideColumnsAsRequired()
  }
  syncConfigToPerspective = async () => {
    log('syncConfigToPerspective');
    try {
      this.config = JSON.parse(this.configEditor?.value || '');
      if (this.config) {
        this.perspectiveViewer?.restore(this.config);
        await this.hideColumnsAsRequired();
        await this._updatePreview();
      }
    } catch (e) {
      log(`Failed to parse config: ${e}`);
      notificationService.warning(`pdq-view#${this.id}|Error parsing JSON: \n${e?.message || e}`);
    }
  };
  syncConfigFromPerspective = async () => {
    log('syncConfigFromPerspective');
    await this._updatePreview();
  };
  render() {
    log(`pdq-data: ${this.id} ${this.show} - render`);
    const show = this.show || pdqHotkeyService.showPdqData.currentValue();
    const slot = html`<slot name="config" @slotchange="${this._onSlotChange}"></slot>`;
    const toolbar = html`
      <cds-icon-button slot="tool-bar" @click="${this.syncConfigFromPerspective}" kind="primary">
        <span slot="tooltip-content"> Sync config from perspective </span>
        ${Upload({ slot: 'icon' })}
      </cds-icon-button>
      <cds-icon-button slot="tool-bar" @click="${this.syncConfigToPerspective}" kind="primary">
        <span slot="tooltip-content"> Sync config to perspective </span>
        ${Download({ slot: 'icon' })}
      </cds-icon-button>
    `;

    return html`
      ${slot}
      ${show ? html`
        <div class="show col ${this.error ? 'error' : ''} m-1">
          ${this.renderToolBar(toolbar)}
          <cds-textarea value="${JSON.stringify(this.config, null, 2)}">
            <label slot="label-text">Config</label>
          </cds-textarea>
        </div>` : nothing}

      <perspective-viewer
        theme=${this.theme}
        style="height: ${this.height}; --column-drop-label--display: block"
        ?selectable="${this.selectable}"
        id="pdq-perspective-viewer-${this.id || this._id}">
      </perspective-viewer>
    `;
  }

  #subscription = Subscription.EMPTY;
  protected async updateData() {
    log('updateData');
    if (this.#table) {
      this.#subscription.unsubscribe()
      log(`awaiting table: ${this.#table}`);
      const table = await tableBroker.getTable(this.#table);
      log(`acquired table: ${this.#table}`);
      await this._perspectivePresent.promise;

      log('loading table');
      this.#subscription = table!.subscribe(async t => {
        log('table changed');
        await this.perspectiveViewer?.load(t);
        log('restoring config', this.config);
        await this.perspectiveViewer?.restore(this.config);
        await this.hideColumnsAsRequired();
        await this._perspectiveLoaded.resolve(true);
        await this._updatePreview();
        this._loaded = true
      });
    } else {
      log('updateData: no table to load...');
    }
  }
  private async _onSlotChange(event: Event) {
    log(`${this.id} - slot change`);
    const slot = event.target as HTMLSlotElement;
    const nodes = slot.assignedNodes({ flatten: true });
    const text = extractTextFromScripts(nodes);
    if (!text.trim()) {
      log(`ignoring blank config`);
      return;
    }
    try {
      this.config = JSON.parse(text);
      await this._perspectiveLoaded.promise
      await this.perspectiveViewer!.restore(this.config);
      await this.hideColumnsAsRequired();
      await this._updatePreview();

    } catch (e: any) {
      log(`WARNING - pdq-view#${this.id}|Error parsing JSON: \n${e?.message || e}`);
      notificationService.warning(`pdq-view#${this.id}|Error parsing JSON: \n${e?.message || e}`);
    }
  }
  private async _updatePreview() {
    await this._perspectiveLoaded.promise
    log('update preview');
    this.config = await this.perspectiveViewer?.save();
    this.dataPreview = `<pdq-perspective-viewer${this.id ? html` id="${this.id}"` : ''}${this.table ? html` table="${this.table}"` : ''}><template>${
      JSON.stringify(this.config, null, 2)}</template></pdq-perspective-viewer>`;
  }
  private styleSheet:CSSStyleSheet|undefined
  hideColumnsAsRequired = async () => {
    if (!this.perspectiveViewer)
      return

    if (!this.styleSheet) {
      const rg = this.perspectiveViewer?.querySelector('perspective-viewer-datagrid')?.shadowRoot
      if (!rg)
        return;

      this.styleSheet = new CSSStyleSheet();
      rg.adoptedStyleSheets = [...rg.adoptedStyleSheets, this.styleSheet];
    }

    const table = tableBroker.getTableMeta(this.table)
    const colsToHide = []

    const index = await table?.index
    if (index && this.hideIndex) {
      colsToHide.push(index)
    }
    if (this.hiddenColumns && this.hiddenColumns.length > 0)
      colsToHide.push(...this.hiddenColumns)
    if (!this.config)
      return
    const style = colsToHide.map(i => {
        let idx = this.config?.columns?.indexOf(i);
        if (idx === -1)
          return ''
        idx++
        console.log(`hiding column ${i} at index ${idx}`);
        return `
          th:nth-child(${idx}) {
            display: none;
          }
          td:nth-child(${idx}) {
            display: none;
          }
        `;
      }).join('')

    console.log('style', style);
    this.styleSheet.replaceSync(style.toString());
  }


  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    log('CHNAGE', _changedProperties)
    this.addEventListeners()
    if (_changedProperties.has('config')) {
      if (this.config && this.perspectiveViewer && this._loaded) {
        this.perspectiveViewer.restore(this.config)
      }
    }
  }
  protected firstUpdated(props: PropertyValues) {
    this.addEventListeners()
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#subscription.unsubscribe();
    tableBroker.removeViewer(this.id);
  }
}

